import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { GaugeChart } from '@/pages/Organization/Analytics/components/GaugeChart'
import { adminApi } from '@/utils/api'
import type { AdminLearningEngagement, AdminDropoutAnalysis } from '@/types/admin'

export default function AdminLearningPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [engagement, setEngagement] = useState<AdminLearningEngagement | null>(null)
  const [dropout, setDropout] = useState<AdminDropoutAnalysis[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        setLoading(true)
        const engagementRes = await adminApi.learningEngagement()
        const engagementData = (engagementRes as { data?: AdminLearningEngagement }).data
        setEngagement(engagementData || null)
        const dropoutRes = await adminApi.learningDropout()
        const dropoutData = (dropoutRes as { data?: AdminDropoutAnalysis[] }).data
        setDropout(Array.isArray(dropoutData) ? dropoutData : [])
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load learning analytics')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [])

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Learning</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>
                Learning engagement, dropout analysis, and reflections.
              </p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {/* Stat Cards */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <StatCard
              title='Enrollments'
              value={loading ? '...' : (engagement?.overview?.totalEnrollments?.toLocaleString() || '0')}
              subtitle='Total enrollments across all modules'
              trend=''
            />
            <StatCard
              title='Completions'
              value={loading ? '...' : (engagement?.overview?.totalCompletions?.toLocaleString() || '0')}
              subtitle='Total completions'
              trend=''
            />
            <StatCard
              title='Dropout Rate'
              value={loading ? '...' : (
                engagement?.overview?.overallCompletionRate
                  ? `${Math.round(100 - engagement.overview.overallCompletionRate)}%`
                  : '0%'
              )}
              subtitle='Overall dropout rate'
              trend=''
            />
          </div>

          {/* Module Engagement */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
            <div className='flex items-center justify-between'>
              <h3 className='text-[18px] font-bold text-[#151515]'>Module Engagement</h3>
              <button className='px-4 py-2 bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold rounded-[8px] text-[14px]'>
                Export
              </button>
            </div>
            {loading && <p className='text-[14px] text-[#4e4d4d]'>Loading engagement data...</p>}
            {!loading && (!engagement?.moduleEngagement || engagement.moduleEngagement.length === 0) && (
              <p className='text-[14px] text-[#4e4d4d]'>No engagement data available.</p>
            )}
            {!loading && engagement?.moduleEngagement && engagement.moduleEngagement.length > 0 && (
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                {engagement.moduleEngagement.map((module) => (
                  <div key={module.moduleId} className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                    <p className='text-[12px] text-[#4e4d4d]'>{module.moduleTitle || `Module ${module.moduleId}`}</p>
                    <p className='text-[18px] font-bold text-[#151515] mt-1'>
                      {Math.round(module.completionRate || 0)}%
                    </p>
                    <p className='text-[12px] text-[#4e4d4d]'>Dropout {Math.round(module.dropoutRate || 0)}%</p>
                    <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full mt-2'>
                      <div
                        className='h-full bg-[#FF6600] rounded-full'
                        style={{ width: `${Math.round(module.completionRate || 0)}%` }}
                      />
                    </div>
                    <p className='text-[10px] text-[#4e4d4d] mt-1'>
                      {module.completions?.toLocaleString() || 0} / {module.enrollments?.toLocaleString() || 0}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <p className='text-[12px] text-[#4e4d4d]'>
              Module-level insights showing enrollments, completions, and dropout rates.
            </p>
          </div>

          {/* Dropout Analysis */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
            <h3 className='text-[18px] font-bold text-[#151515]'>Dropout Analysis</h3>
            {loading && <p className='text-[14px] text-[#4e4d4d]'>Loading dropout data...</p>}
            {!loading && dropout.length > 0 && (
              <>
                <div className='mb-4'>
                  <GaugeChart
                    value={Math.round(dropout.reduce((sum, d) => sum + (d.averageDropoutPosition || 0), 0) / dropout.length || 76)}
                    label='Avg Completion Point'
                  />
                </div>
                <p className='text-[14px] text-[#4e4d4d]'>
                  Highlight where users abandon content to guide content improvements.
                </p>
                <div className='space-y-2'>
                  <h4 className='text-[14px] font-bold text-[#151515]'>Dropout by Module:</h4>
                  {dropout.map((mod) => (
                    <div key={mod.moduleId} className='flex justify-between items-center text-[14px] p-3 rounded-[10px] bg-white border border-[#e8e8e8]'>
                      <span className='text-[#151515] font-medium'>{mod.moduleTitle || mod.moduleId}</span>
                      <span className='text-[#4e4d4d]'>
                        {Math.round(mod.dropoutRate || 0)}% dropout at {Math.round(mod.averageDropoutPosition || 0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {!loading && dropout.length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No dropout data available.</p>
            )}
          </div>

          {error && <p className='text-[14px] text-red-600'>{error}</p>}
        </div>
      </div>
    </div>
  )
}
